import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
    VStack,
    HStack,
    FormControl,
    WarningIcon
} from 'native-base';
import React, { useEffect, useState } from 'react';
import { Caption } from '~/shared/Typography';
import Pressable from '~/shared/Pressable';
import {
    useSendEmailVerificationCode,
    useVerifyEmailCode,
    useSpringBigStore,
    i18nFn,
    useTheme,
} from '@springbig/shared';
import { Button, Input, CheckBox, } from '~/shared/Theme';

type EmailVerificationFormProps = {
    t: i18nFn
    isRequiredTerms?: boolean;
    walletId: string;
    onCancel: () => void;
    onProceed: (validatedEmail: string) => void;
    emailVerificationFormType?: EmailVerificationFormType;
};

type ConsumerApiError = {
    data?: {
        errors?: string;
    };
};

type EmailVerificationFormType = 'springbigpay' | 'ordersPage' | 'giftCardInbox';

const EmailVerificationForm = ({
    t,
    isRequiredTerms,
    walletId,
    onCancel,
    onProceed,
    emailVerificationFormType = 'springbigpay'
}: EmailVerificationFormProps) => {

    const {
        highlight_text,
        field_background,
        field_color,
        field_border,
    } = useTheme();

    const { member } = useSpringBigStore();
    const [apiError, setApiError] =
        useState<string | null>(null);

    const [isPinCodeStep, setIsPinCodeStep] =
        useState<boolean>(false);

    const [isTermsAcknowledged, setIsTermsAcknowledged] =
        useState<boolean>(false);

    const [termsUnchecked, setTermsUnchecked] =
        useState<boolean>(false);

    const [currentEmail, setCurrentEmail] =
        useState<string | null>(null);

    const [isResentCode, setIsResentCode] =
        useState<boolean>(false);

    const schemaEmail = yup.object().shape({
        email: yup
            .string()
            .required(t('validation.email_required'))
            .matches(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i, t('validation.email_invalid'))
            .trim()
    });

    const schemaCode = yup.object().shape({
        code: yup
            .string()
            .required(t('validation.code_required'))
            .matches(/.{6}/i, t('validation.code_invalid'))
            .trim()
    });

    type FormDataEmail = yup.InferType<typeof schemaEmail>;
    type FormDataCode = yup.InferType<typeof schemaCode>;

    const {
        control: controlEmail,
        handleSubmit: handleSubmitEmail,
        formState: { errors: errorsEmail }
    } = useForm({
        resolver: yupResolver(schemaEmail)
    });

    const {
        control: controlCode,
        handleSubmit: handleSubmitCode,
        formState: { errors: errorsCode }
    } = useForm({
        resolver: yupResolver(schemaCode)
    });

    const { field: emailField } = useController({
        control: controlEmail,
        defaultValue: currentEmail || '',
        name: 'email',
    });

    const { field: codeField } = useController({
        control: controlCode,
        defaultValue: '',
        name: 'code',
    });

    const {
        mutate: sendVerificationCode,
        isLoading: isLoadingEmail,
        error: errorEmail
    } = useSendEmailVerificationCode();

    const {
        mutate: verifyEmailCode,
        isLoading: isLoadingCode,
        error: errorCode
    } = useVerifyEmailCode();

    useEffect(() => {

        if (errorEmail) {
            setApiError((errorEmail as ConsumerApiError)?.data?.errors);
        }

        if (errorCode) {
            setApiError((errorCode as ConsumerApiError)?.data?.errors);
        }
    }, [errorCode, errorEmail]);

    const onSubmitEmail = (data: FormDataEmail): void => {

        if (isRequiredTerms && !isTermsAcknowledged) {
            setTermsUnchecked(true);
            return;
        }

        setApiError(null);
        setTermsUnchecked(false);
        setCurrentEmail(data.email);

        sendVerificationCode({
            email: data.email,
            member_id: walletId,
        }, {
            onSuccess: () => {

                setIsPinCodeStep(true);
            }
        });
    };

    const resendCode = (): void => {

        if (isResentCode) {
            return;
        }

        setApiError(null);
        sendVerificationCode({
            email: currentEmail,
            member_id: walletId,
        }, {
            onSuccess: () => {

                setIsResentCode(true);
                setTimeout(() => setIsResentCode(false), 1000 * 60);
            }
        });
    };

    const onSubmitCode = (data: FormDataCode): void => {

        setApiError(null);
        verifyEmailCode({
            email: currentEmail,
            code: data.code,
            member_id: walletId,
        }, {
            onSuccess: () => {

                onProceed(currentEmail);
            }
        });
    };

    const handleLinkPress = (url: string): void => {

        if (!url) {
            return;
        }

        window.open(url, '_blank');
    };

    return (
        <VStack space={8}>
            { !isPinCodeStep ?
                <VStack space={8}>
                    <FormControl isInvalid={!!errorsEmail.email}>
                        <FormControl.Label>
                            <Caption text={'subscriptions.email_field'} />
                        </FormControl.Label>
                        <Input
                            {...emailField}
                            isInvalid={!!errorsEmail.email}
                            isDisabled={isLoadingEmail}
                            maxLength={255}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { errorsEmail.email?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    { isRequiredTerms &&
                        <FormControl isInvalid={termsUnchecked}>
                            <HStack>
                                <CheckBox
                                    flex={1}
                                    marginRight={2}
                                    alignSelf={'flex-start'}
                                    isChecked={isTermsAcknowledged}
                                    value={isTermsAcknowledged.toString()}
                                    isDisabled={isLoadingEmail}
                                    onChange={(value) => {

                                        if (value) {
                                            setIsTermsAcknowledged(value);
                                        }
                                    }}
                                />
                                <Pressable
                                    onPress={() => handleLinkPress(member.subs_terms)}
                                    flexDirection={'row'}
                                    flexWrap={'wrap'}
                                    flex={1}
                                >
                                    <Caption text={'subscriptions.terms_prefix'} />
                                    <Caption
                                        color={highlight_text}
                                        text={'subscriptions.terms_suffix'}
                                    />
                                </Pressable>
                            </HStack>
                            <FormControl.ErrorMessage
                                leftIcon={<WarningIcon size={3} />}
                            >
                                { t('subscriptions.terms_error') }
                            </FormControl.ErrorMessage>
                        </FormControl>
                    }

                    <Button
                        text={emailVerificationFormType === 'giftCardInbox' ? 'giftCard.inboxSendCodeButton' : 'subscriptions.next'}
                        onPress={handleSubmitEmail(onSubmitEmail)}
                        loading={isLoadingEmail}
                    />

                </VStack>
                :
                <VStack space={4}>
                    <FormControl isInvalid={!!errorsCode.code}>
                        <Caption
                            textAlign={'center'}
                            text={'subscriptions.code_sent'}
                        />
                        <FormControl.Label>
                            <Caption text={'subscriptions.code_field'} />
                        </FormControl.Label>
                        <Input
                            {...codeField}
                            isInvalid={!!errorsCode.code}
                            isDisabled={isLoadingCode}
                            maxLength={6}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { errorsCode.code?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    <HStack justifyContent={'space-evenly'} space={4} flexWrap={'wrap'} alignItems={'center'}>
                        <Button
                            variant={'text'}
                            onPress={resendCode}
                            loading={isLoadingEmail}
                            disabled={isLoadingCode}
                            text={isResentCode ? 'subscriptions.code_sent' : 'subscriptions.code_resend'}
                        />

                        <Button
                            variant={'text'}
                            disabled={isLoadingCode}
                            onPress={() => {

                                setApiError(null);
                                setIsPinCodeStep(false);
                            }}
                            text={'subscriptions.change_email'}
                        />
                    </HStack>

                    <Button
                        onPress={handleSubmitCode(onSubmitCode)}
                        loading={isLoadingCode}
                        text={'subscriptions.verify_email'}
                    />
                </VStack>
            }

            { emailVerificationFormType === 'springbigpay' &&
                <VStack space={8}>
                    <Button
                        variant={'neutral'}
                        onPress={onCancel}
                        disabled={isLoadingCode || isLoadingEmail}
                        text={'subscriptions.back_button'}
                    />

                    <FormControl isInvalid={!!apiError}>
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={5} />}
                        >
                            { t('subscriptions.email_error', [apiError]) }
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
            }
        </VStack>
    );
};

export default EmailVerificationForm;
